import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPADataTable from '../shared/SPADataTable';
import SPAPill from '../shared/SPAPill';
import SPASectionHeading from '../shared/SPASectionHeading';
import { ThrottledUser } from './types';

// The TTL is what separates the two refusals: the per-minute burst cap clears in seconds, the daily
// budget holds until midnight Pacific. Saying which one it is saves an operator from granting
// headroom for something that would have cleared on its own.
const isBurstCap = (seconds: number) => seconds < 300;

const describeExpiry = (seconds: number) => {
  if (seconds <= 0) return 'now';
  if (isBurstCap(seconds)) return `~${seconds}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return `${hours > 0 ? `${hours}h ` : ''}${mins}m`;
};

const columnHelper = createColumnHelper<ThrottledUser>();

const columns = [
  columnHelper.accessor('name', { header: 'User' }),
  columnHelper.accessor('identity', {
    cell: info => <span className="tw-font-mono tw-text-xs">{info.getValue()}</span>,
    header: 'Identity',
  }),
  columnHelper.accessor('expires_in', {
    cell: info => (
      <SPAPill tone="warning">{isBurstCap(info.getValue()) ? 'Burst cap' : 'Daily budget'}</SPAPill>
    ),
    header: 'Limit',
    id: 'limit',
  }),
  columnHelper.accessor('expires_in', {
    cell: info => describeExpiry(info.getValue()),
    header: 'Clears in',
    id: 'clears',
    meta: { align: 'right' },
  }),
] as ColumnDef<ThrottledUser, unknown>[];

const ThrottledNow = ({ users }: { users?: ThrottledUser[] }) => (
  <section>
    <SPASectionHeading title="Limited now" />
    <SPADataTable
      columns={columns}
      data={users ?? []}
      defaultSort={[]}
      emptyMessage="Nobody is being refused right now."
      isLoading={!users}
      loadingRows={3}
    />
  </section>
);

export default ThrottledNow;
